import axios from "axios";
import { toast } from "sonner";
import { API_BASE_URL, ENDPOINTS } from "../config/endpoints";

const API = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

API.interceptors.request.use((config) => {
  const userStr = localStorage.getItem("user");
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }
    } catch (error) {
      // Ignore parse errors
    }
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

// Centralized Response Interceptor for Toasts & Auth Refresh
API.interceptors.response.use(
  (response) => {
    const { method, url } = response.config;
    const toastId = url?.split("?")[0] || "api-success";
    const successMsg = (response.config as any).toastSuccess;

    if (successMsg) {
      toast.success(successMsg, { id: toastId });
    } else if (method !== "get" && (response.data?.message || response.data?.detail)) {
      toast.success(response.data.message || response.data.detail, { id: toastId });
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url;
    const toastId = url?.split("?")[0] || "api-error";

    // 🔐 Handle 401 Unauthorized (Token Expired)
    if (error.response?.status === 401 && !originalRequest._retry && !url?.includes("/login") && !url?.includes("/refresh")) {
      originalRequest._retry = true;

      try {
        const userStr = localStorage.getItem("user");
        const user = userStr ? JSON.parse(userStr) : null;

        if (user && user.token === "fake-token") {
          console.warn("[AUTH] Mock token detected. Bypassing 401 logout to prevent kick-out.");
          return Promise.resolve({ data: [] });
        }

        const refreshToken = user?.refresh_token;

        if (!refreshToken) {
          // No refresh token available - cannot recover session
          throw new Error("No refresh token");
        }

        // 🔄 Singleton Refresh Logic: Multiple parallel 401s will all wait for the SAME promise.
        if (!refreshPromise) {
          console.log("[AUTH] Initiating singleton refresh...");
          refreshPromise = axios.post(`${API_BASE_URL}${ENDPOINTS.AUTH.REFRESH}`, {
            refresh_token: refreshToken,
          }).then(res => {
            const newToken = res.data.access_token;
            user.token = newToken;
            localStorage.setItem("user", JSON.stringify(user));
            console.log("[AUTH] Singleton refresh successful.");
            refreshPromise = null;
            return newToken;
          }).catch(err => {
            refreshPromise = null;
            throw err;
          });
        }

        const newAccessToken = await refreshPromise;
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        console.error("[AUTH] Terminal failure. Clearing session:", refreshError);
        localStorage.removeItem("user");
        localStorage.removeItem("active-page");
        window.location.href = "/";
        return Promise.reject(refreshError);
      }
    }

    // Standard Error Toasting
    let message = "An error occurred";
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      if (status === 401 || status === 403) {
        message = "Unauthorized access. Please login again.";
      } else {
        message = data?.detail || data?.message || `Server Error: ${status}`;
      }
    } else if (error.request) {
      message = "Network error. Please check your internet connection.";
    } else {
      message = error.message;
    }

    toast.error(message, { id: toastId });
    return Promise.reject(error);
  }
);

export default API;

